import type { ProductsRecord } from "@/utils/PocketBaseAdapter";
import { pb } from "@/utils/PocketBaseAdapter";
import { useUserStore } from "@/utils/UserStore";
import { moment } from "@/utils/useTimeZone";

export const useTodPricing = defineStore("TOD", () => {
  // Data
  const todList = ref<any[]>([]);
  const totalTodListCount = ref<number>(0);
  const lastFetchFilter = ref<string>("");

  const userStore = useUserStore();
  const { currentLocation, timeZone } = storeToRefs(userStore);

  // Methods
  const addTodToProduct = async (product: ProductsRecord, user?: any, membership?: any) => {
    // Create a shallow copy of the product to avoid mutating the original
    const productCopy = { ...product };
    const { todAdjustment, todSource } = getItemTOD(productCopy, moment(), user, membership);

    productCopy.todAdjustment = todAdjustment;
    productCopy.todSource = todSource;
    return productCopy;
  };

  const getItemTOD = (item: any, dateTime: any, user?: any, membership?: any, location?: string) => {
    let todAdjustment = 0;
    let todSource = "";

    const locationToUse = location || item.location || currentLocation.value;

    // Activity TODs sit directly on the item; products fall back to subcategory then category
    const activityTodPricing = item?.expand?.TODPricing || [];
    const subCategoryTodPricing = (item?.expand?.subCategory?.expand?.TODPricing || []).filter((tod: any) => tod.location === locationToUse);
    const categoryTodPricing = (item?.expand?.category?.expand?.TODPricing || []).filter((tod: any) => tod.location === locationToUse);
    const todPricing = activityTodPricing.length > 0
      ? activityTodPricing
      : subCategoryTodPricing.length > 0 ? subCategoryTodPricing : categoryTodPricing;

    if (todPricing.length > 0) {
      const applicableTods = todPricing.filter((tod: any) => isTimeWithinTOD(tod, dateTime, user, membership));
      if (applicableTods.length > 0) {
        // Discount always wins over an increase; if multiple discounts take the deepest;
        // if only increases, take the lowest
        const winningTod = applicableTods.reduce((best: any, tod: any) => {
          return tod.percentChange < best.percentChange ? tod : best;
        });
        todAdjustment = winningTod.percentChange;
        todSource = winningTod.id;

        if (todAdjustment < -item.maxDiscount) {
          todAdjustment = -item.maxDiscount;
        }
      }
    }

    return { 'todAdjustment':todAdjustment, 'todSource': todSource };
  }

  // Function to check if the current time falls within any of the TOD periods
  const isTimeWithinTOD = (tod: any, time?: any, user?: any, membership?: any) => {
    time = time ? time : moment();
    const currentDay = time.format("dddd").toLowerCase(); // Get current day of the week

    // Check if current day is active in this TOD
    if (!tod[currentDay]) return false;

    // Check if current date is within validFrom and validTill range
    if (tod.validFrom) {
      const validFromDate = moment(tod.validFrom).startOf("day");
      if (time.isBefore(validFromDate)) return false;
    }

    if (tod.validTill) {
      const validTillDate = moment(tod.validTill).endOf("day");
      if (time.isAfter(validTillDate)) return false;
    }

    // Check appliesTo field
    // If user/membership not explicitly passed, fall back to current auth state
    const effectiveUser = user !== undefined ? user : pb.authStore.model;
    const effectiveMembership = membership !== undefined ? membership : pb.authStore.model?.membership;

    if (tod.appliesTo) {
      if (tod.appliesTo === "No One") return false;
      if (tod.appliesTo === "Memberships" && !effectiveMembership) return false;
      if (tod.appliesTo === "Users" && !effectiveUser) return false;
      // "Everyone" passes through
    }

    // Set start and end times for the TOD using the DATE from the time parameter
    const startTime = time.clone().set({
      hour: tod.startHour,
      minute: tod.startMin,
      second: 0,
      millisecond: 0,
    });

    const endTime = time.clone().set({
      hour: tod.finishHour,
      minute: tod.finishMin,
      second: 0,
      millisecond: 0,
    });

    // Check if the current time is within the start and end time
    return time.isBetween(startTime, endTime, undefined, "[)");
  };

  const fetchTodPricing = async (params: any, force = false) => {
    try {
      // Skip if already fetched for the same filter
      if (!force && params?.filter && params.filter === lastFetchFilter.value) return;

      const records = await pb.collection("TODPricing").getFullList({
        ...params,
        expand: "productCategories_via_TODPricing, productSubCategories_via_TODPricing, activities_via_TODPricing",
      });
      todList.value = records || [];
      totalTodListCount.value = todList.value.length;
      lastFetchFilter.value = params?.filter || "";
    } catch (e) {
      console.error("Error fetching TOD pricing:", e);
    }
  };

  return {
    todList,
    totalTodListCount,
    fetchTodPricing,
    isTimeWithinTOD,
    addTodToProduct,
    getItemTOD,
  };
});
